import {
  Autocomplete,
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  type AutocompleteRenderInputParams,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback, type ChangeEvent, type SyntheticEvent } from "react";
import { OrderStatus } from "../../../generated/graphql";
import {
  MAX_PICKED_ORDERS,
  orderStatusLabel,
  type BenchOrderOption,
  type BenchOrderSelectionMode,
} from "../bench-run";
import { BenchOrdersPicker } from "./bench-order-picker";

const styles = {
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 2,
    alignItems: "start",
  } satisfies SxProps<Theme>,
};

const ORDER_STATUSES = Object.values(OrderStatus);

const renderStatusInput = (params: AutocompleteRenderInputParams) => (
  <TextField
    {...params}
    helperText="Optional. Narrows the orders you can pick, or the newest orders taken."
    label="Order status"
  />
);

type ScenarioOrderSectionProps = {
  readonly partnershipUuid: string | null;
  readonly mode: BenchOrderSelectionMode;
  readonly onModeChange: (mode: BenchOrderSelectionMode) => void;
  readonly pickedOrders: BenchOrderOption[];
  readonly onPickedOrdersChange: (orders: BenchOrderOption[]) => void;
  readonly orderStatuses: OrderStatus[];
  readonly onOrderStatusesChange: (statuses: OrderStatus[]) => void;
  readonly limit: string;
  readonly onLimitChange: (limit: string) => void;
};

/**
 * Chooses the orders a Scenario run covers: orders the operator picks, or the
 * partnership's newest orders up to a limit.
 */
const ScenarioOrderSection = ({
  partnershipUuid,
  mode,
  onModeChange,
  pickedOrders,
  onPickedOrdersChange,
  orderStatuses,
  onOrderStatusesChange,
  limit,
  onLimitChange,
}: ScenarioOrderSectionProps) => {
  const handleModeChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>, value: string) => {
      onModeChange(value === "newest" ? "newest" : "picked");
    },
    [onModeChange],
  );
  const handleStatusesChange = useCallback(
    (_: SyntheticEvent, value: OrderStatus[]) => {
      onOrderStatusesChange(value);
    },
    [onOrderStatusesChange],
  );
  const handleLimitChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onLimitChange(event.target.value);
    },
    [onLimitChange],
  );

  return (
    <Stack spacing={2}>
      <RadioGroup row value={mode} onChange={handleModeChange}>
        <FormControlLabel
          control={<Radio />}
          label="Pick orders"
          value="picked"
        />
        <FormControlLabel
          control={<Radio />}
          label="Use the newest orders"
          value="newest"
        />
      </RadioGroup>
      <Box sx={styles.row}>
        {mode === "picked" ? (
          <BenchOrdersPicker
            helperText={`Search by order name, HAWB or MAWB. Pick up to ${MAX_PICKED_ORDERS}.`}
            label="Orders"
            orderStatuses={orderStatuses}
            partnershipUuid={partnershipUuid}
            value={pickedOrders}
            onChange={onPickedOrdersChange}
          />
        ) : (
          <TextField
            helperText="How many of the partnership's newest orders to run, 1 to 50."
            label="Limit"
            type="number"
            value={limit}
            onChange={handleLimitChange}
          />
        )}
        <Autocomplete
          disableCloseOnSelect
          multiple
          getOptionLabel={orderStatusLabel}
          options={ORDER_STATUSES}
          renderInput={renderStatusInput}
          value={orderStatuses}
          onChange={handleStatusesChange}
        />
      </Box>
    </Stack>
  );
};

export default ScenarioOrderSection;
